// The four wizard steps. Each is a <form>: Enter or "Continue" submits it,
// and the page moves on only when the step is valid.

import type { ReactNode } from 'react'
import { ADDON_KEYS, type SportCatalogEntry } from '@/api/types'
import { PLATFORM_DOMAIN } from '@/lib/tenant'
import { Button, FormField, RadioCards, SkeletonRows, TextInput, Textarea, Toggle } from '@/components/ui'
import { AlertIcon, CheckIcon, SpinnerIcon } from '@/components/ui/icons'
import { ADDONS } from '@/lib/addons'
import { cn, formatDate } from '@/lib/format'
import { MAX_TRIAL_DAYS, effectiveSubdomain, packageAddons, trialDaysError, type Draft, type PackageChoice } from './draft'
import type { SubdomainCheckState } from './useSubdomainCheck'

export type FieldErrors = Partial<Record<keyof Draft, string>>

interface StepProps {
  draft: Draft
  update: (patch: Partial<Draft>) => void
  errors: FieldErrors
  onBack?: () => void
  onNext: () => void
}

function StepActions({ onBack, nextLabel = 'Continue', nextDisabled, pending, children }: { onBack?: () => void; nextLabel?: string; nextDisabled?: boolean; pending?: boolean; children?: ReactNode }) {
  return (
    <div className="mt-6 flex flex-col-reverse gap-2 border-t border-line pt-4 sm:flex-row sm:items-center sm:justify-end">
      {children ? <div className="sm:mr-auto">{children}</div> : null}
      {onBack ? (
        <Button onClick={onBack} disabled={pending}>
          Back
        </Button>
      ) : null}
      <Button type="submit" variant="primary" disabled={nextDisabled} loading={pending}>
        {nextLabel}
      </Button>
    </div>
  )
}

// ─── 1. Name & subdomain ────────────────────────────────────────────────────

function SubdomainStatus({ check, host }: { check: SubdomainCheckState; host: string }) {
  switch (check.status) {
    case 'empty':
      return <span className="text-muted">Becomes {`<subdomain>.${PLATFORM_DOMAIN}`}. Lowercase letters, numbers and hyphens.</span>
    case 'checking':
      return (
        <span className="inline-flex items-center gap-1.5 text-muted">
          <SpinnerIcon size={12} /> Checking {host}…
        </span>
      )
    case 'available':
      return (
        <span className="inline-flex items-center gap-1.5 text-good">
          <CheckIcon size={12} /> {host} is available
        </span>
      )
    case 'unavailable':
    case 'invalid':
      return (
        <span className="inline-flex items-start gap-1.5 text-bad">
          <AlertIcon size={12} className="mt-0.5 shrink-0" /> <span>{check.message}</span>
        </span>
      )
    case 'error':
      return (
        <span className="inline-flex items-start gap-1.5 text-warn">
          <AlertIcon size={12} className="mt-0.5 shrink-0" /> <span>{check.message}</span>
        </span>
      )
  }
}

export function BasicsStep({ draft, update, errors, onNext, check, canContinue }: StepProps & { check: SubdomainCheckState; canContinue: boolean }) {
  const sub = effectiveSubdomain(draft)
  const host = `${sub}.${PLATFORM_DOMAIN}`
  const subError = errors.subdomain
  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        onNext()
      }}
    >
      <div className="grid grid-cols-1 gap-5">
        <FormField label="League name" required error={errors.name}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              invalid={invalid}
              value={draft.name}
              maxLength={120}
              autoComplete="off"
              onChange={(e) => update({ name: e.target.value })}
              placeholder="Ironbridge Basketball League"
            />
          )}
        </FormField>
        <FormField label="Subdomain" required error={subError}>
          {({ id, describedBy, invalid }) => (
            <div className="grid gap-1.5">
              {/* The border is on this wrapper, so the input is 34px and the box matches the 36px TextInput above. */}
              <div className={cn('flex min-w-0 items-stretch overflow-hidden rounded-md border bg-raised focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/25', invalid || check.status === 'unavailable' || check.status === 'invalid' ? 'border-bad/70' : 'border-line-strong')}>
                <input
                  id={id}
                  aria-describedby={[describedBy, `${id}-status`].filter(Boolean).join(' ')}
                  aria-invalid={invalid || check.status === 'unavailable' || check.status === 'invalid' || undefined}
                  className="h-[34px] min-w-0 flex-1 bg-transparent px-3 font-mono text-[13px] text-ink placeholder:text-subtle focus:outline-none max-md:text-base"
                  autoCapitalize="off"
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={63}
                  value={draft.subdomainTouched ? draft.subdomain : sub}
                  onChange={(e) => update({ subdomain: e.target.value.toLowerCase(), subdomainTouched: true })}
                  placeholder="ironbridge"
                />
                <span aria-hidden className="flex shrink-0 items-center border-l border-line bg-panel px-2.5 font-mono text-xs text-muted">
                  .{PLATFORM_DOMAIN}
                </span>
              </div>
              <p id={`${id}-status`} role="status" aria-live="polite" className="text-xs">
                <SubdomainStatus check={check} host={host} />
              </p>
            </div>
          )}
        </FormField>
      </div>
      <StepActions nextDisabled={!canContinue} />
    </form>
  )
}

// ─── 2. Sport ───────────────────────────────────────────────────────────────

export function SportStep({ draft, update, onBack, onNext, sports, errors }: StepProps & { sports: SportCatalogEntry[] | undefined }) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        onNext()
      }}
    >
      {!sports ? (
        <SkeletonRows rows={2} label="Loading sports" />
      ) : (
        <RadioCards
          legend="Primary sport"
          value={draft.sport}
          onChange={(sport) => update({ sport })}
          columns={2}
          options={sports.map((s) => ({
            value: s.id,
            label: s.display_name,
            description: `Turns on the ${s.display_name} pack: homepage, theme, sponsor tiers and score-bug template, so the league admin works at once.`,
          }))}
        />
      )}
      {errors.sport ? <p className="mt-2 text-xs text-bad">{errors.sport}</p> : null}
      <p className="mt-3 text-xs text-muted">More sports can be added later on the tenant's Sports tab.</p>
      <StepActions onBack={onBack} nextDisabled={!sports} />
    </form>
  )
}

// ─── 3. First admin ─────────────────────────────────────────────────────────

export function AdminStep({ draft, update, errors, onBack, onNext }: StepProps) {
  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        onNext()
      }}
    >
      <div className="grid grid-cols-1 gap-5">
        <label className="flex items-start gap-2.5 text-[13px]">
          <input type="checkbox" className="mt-0.5 h-4 w-4 shrink-0 accent-[rgb(var(--accent-solid))]" checked={draft.skipAdmin} onChange={(e) => update({ skipAdmin: e.target.checked })} />
          <span>
            <span className="font-semibold text-ink">Skip, invite a league admin later</span>
            <span className="block text-xs text-muted">The dashboard flags the tenant as having no admin until someone accepts an invite from its People tab.</span>
          </span>
        </label>
        {!draft.skipAdmin ? (
          <FormField
            label="Email"
            required
            error={errors.adminEmail}
            hint="We'll email an invite to join as league admin. They create an account or sign in with one they have."
          >
            {({ id, describedBy, invalid }) => (
              <TextInput
                id={id}
                type="email"
                aria-describedby={describedBy}
                invalid={invalid}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                value={draft.adminEmail}
                onChange={(e) => update({ adminEmail: e.target.value })}
                placeholder="organiser@example.com"
              />
            )}
          </FormField>
        ) : null}
      </div>
      <StepActions onBack={onBack} />
    </form>
  )
}

// ─── 4. Plan & trial ────────────────────────────────────────────────────────

export function PlanStep({ draft, update, errors, onBack, onNext, pending }: StepProps & { pending: boolean }) {
  const trialErr = errors.trialDays ?? null
  const days = Number(draft.trialDays)
  const endsAt = !trialDaysError(draft.trialDays) ? new Date(Date.now() + days * 86_400_000).toISOString() : null
  const defaults = packageAddons(draft.packageKey)
  const differs = ADDON_KEYS.some((k) => draft.addons[k] !== defaults[k])
  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        onNext()
      }}
    >
      <div className="grid grid-cols-1 gap-6">
        <FormField label="Trial length (days)" required error={trialErr} hint={endsAt ? `Trial ends ${formatDate(endsAt)}. Up to ${MAX_TRIAL_DAYS} days.` : `1–${MAX_TRIAL_DAYS} days.`}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_TRIAL_DAYS}
              aria-describedby={describedBy}
              invalid={invalid}
              value={draft.trialDays}
              onChange={(e) => update({ trialDays: e.target.value })}
              className="sm:w-32"
            />
          )}
        </FormField>

        <div className="grid gap-1.5">
          <RadioCards<PackageChoice>
            legend="Package"
            value={draft.packageKey}
            columns={2}
            onChange={(packageKey) => update(draft.addonsTouched ? { packageKey } : { packageKey, addons: packageAddons(packageKey) })}
            options={[
              { value: 'standard', label: 'Standard', description: 'Includes Broadcast, Fan AI, Forms and White-label. Other add-ons on top.' },
              { value: 'none', label: 'No package', description: 'Add-ons only: pick exactly what they get.' },
            ]}
          />
          {errors.packageKey ? <p className="text-xs text-bad">{errors.packageKey}</p> : null}
        </div>

        <fieldset className="grid min-w-0 gap-2">
          <legend className="mb-2 flex w-full flex-wrap items-baseline justify-between gap-2 text-xs font-semibold text-ink">
            <span>Add-ons for the trial</span>
            {differs ? (
              <button type="button" className="text-xs font-semibold text-accent hover:underline" onClick={() => update({ addons: defaults, addonsTouched: false })}>
                Reset to package default
              </button>
            ) : null}
          </legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {ADDON_KEYS.map((k) => (
              <div key={k} className="flex min-w-0 items-center justify-between gap-3 rounded-md border border-line bg-raised/50 px-3 py-2 text-[13px]">
                <span className="min-w-0">
                  <span id={`addon-${k}`} className="block font-semibold text-ink">
                    {ADDONS[k].label}
                  </span>
                  <span className="block text-2xs text-muted">{ADDONS[k].description}</span>
                </span>
                <Toggle size="sm" labelledBy={`addon-${k}`} checked={draft.addons[k]} onChange={(v) => update({ addons: { ...draft.addons, [k]: v }, addonsTouched: true })} />
              </div>
            ))}
          </div>
          {errors.addons ? <p className="text-xs text-bad">{errors.addons}</p> : null}
        </fieldset>

        <FormField label="Notes" error={errors.notes} hint="Internal. Only super-admins see this.">
          {({ id, describedBy, invalid }) => (
            <Textarea id={id} aria-describedby={describedBy} invalid={invalid} value={draft.notes} onChange={(e) => update({ notes: e.target.value })} placeholder="e.g. Met at the September tournament; wants ticketing for playoffs" />
          )}
        </FormField>
      </div>
      <StepActions onBack={onBack} nextLabel="Create tenant" pending={pending} />
    </form>
  )
}
